import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { MatDialog } from '@angular/material/dialog';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import {
  CalendarApi,
  CalendarOptions,
  DateSelectArg,
  DateSpanApi,
  DatesSetArg,
  EventClickArg,
  EventDropArg,
  EventMountArg,
} from '@fullcalendar/core/index.js';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { ClientEventUpdateModel } from './models/event-operations.model';
import { CalendarViewType } from './models/scheduler.model';

import { MatTooltip } from '@angular/material/tooltip';
import { EventResizeDoneArg } from '@fullcalendar/interaction';

import { DatePipe, KeyValue, KeyValuePipe, TitleCasePipe } from '@angular/common';
import { MatCalendar } from '@angular/material/datepicker';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import {
  MatDrawer,
  MatDrawerContainer,
  MatDrawerContent,
} from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { EventImpl } from '@fullcalendar/core/internal';
import {
  addDays,
  addMilliseconds,
  addMinutes,
  areIntervalsOverlapping,
  differenceInMilliseconds,
  secondsToMilliseconds,
  startOfDay,
  startOfISOWeek,
  startOfToday,
  subDays,
} from 'date-fns';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  filter,
  finalize,
  first,
  forkJoin,
  interval,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  skip,
  Subject,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  ConnectionState,
  HasPermissionDirective,
  PermissionService,
  triggerIfSlow,
  WebSocketService,
} from '@ipg/ngx-frontend-toolset';
import { ConfirmHandler } from '../../components/dialog/dialog.model';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';

import { GlobalLoadersService } from '../../global-loaders.service';
import {
  ClientEditorWebSocketPayload,
  ClientEventConflictResponseModel,
  ClientEventResponseModel,
} from '../../models/clients.model';
import { IdleDetectionService } from '../../services/idle-detection.service';

import { farthestMinute } from '../../utils/math';
import { ClientEditorListComponent } from './components/client-editor-list/client-editor-list.component';
import { EventDialogComponent } from './components/event-dialog/event-dialog.component';
import {
  CopyEventsDialogComponent,
  CopyEventsDialogData,
  CopyEventsDialogResult,
} from './components/copy-events-dialog/copy-events-dialog.component';
import { SchedulerLegendComponent } from './components/scheduler-legend/scheduler-legend.component';
import { CalendarManagerService } from './services/calendar-manager.service';
import { ClientEditorService } from './services/client-editor.service';
import { EventDialogService } from './services/event-dialog-service.service';
import { EventOperationsService } from './services/event-operations.service';
import { RangeClaimManagerService } from './services/range-claim-manager.service';

@Component({
  selector: 'app-scheduler',
  imports: [
    MatButton,
    MatCalendar,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatToolbar,
    MatTooltip,
    FullCalendarModule,
    DatePipe,
    MapFromPipe,
    TitleCasePipe,
    KeyValuePipe,
    SchedulerLegendComponent,
    MatDivider,
    ClientEditorListComponent,
    HasPermissionDirective,
  ],
  templateUrl: './scheduler.component.html',
  styleUrl: './scheduler.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulerComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly LOADER_CALLER_ID = 'scheduler_loader_caller';
  private static readonly ACTIVITY_UPDATE_INTERVAL_MS = 30_000; // 30 seconds, easily changeable
  private readonly _calendarManager = inject(CalendarManagerService);
  private readonly _rangeClaimManager = inject(RangeClaimManagerService);
  private readonly _eventOperations = inject(EventOperationsService);
  private readonly _clientEditorService = inject(ClientEditorService);
  private readonly _idleDetectionService = inject(IdleDetectionService);
  private readonly _wsService = inject(WebSocketService);
  private readonly _dialog = inject(DialogService);
  private readonly _eventDialog = inject(EventDialogService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _globalLoaders = inject(GlobalLoadersService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _matDialog = inject(MatDialog);

  /** Permission flags for client event actions */
  private readonly _canCreate =
    this._permissionService.hasPermission('client_event:create');
  private readonly _canUpdate =
    this._permissionService.hasPermission('client_event:update');
  private readonly _canDelete =
    this._permissionService.hasPermission('client_event:delete');

  /** True when the user has no create/update/delete permissions (view-only). */
  protected readonly isReadOnly =
    !this._canCreate && !this._canUpdate && !this._canDelete;

  private readonly _clientId$ = this._route.parent!.paramMap.pipe(
    map((params) => params.get('clientId')),
    filter((clientId) => clientId != null)
  );

  private readonly _clientId = toSignal(this._clientId$);

  private readonly _calendar = viewChild.required(FullCalendarComponent);

  protected readonly calendarTitle = signal('');

  protected readonly calendarOptions = signal<CalendarOptions>(
    this._calendarManager.buildCalendarOptions({
      eventClick: this._handleEventClick.bind(this),
      select: this._handleDateSelect.bind(this),
      selectAllow: this._handleDateSelectAllow.bind(this),
      datesSet: this._handleDatesSet.bind(this),
      eventResize: this._handleEventResize.bind(this),
      eventDrop: this._handleEventDrop.bind(this),
      eventDidMount: this._handleEventDidMount.bind(this),
      eventAllow: (dropInfo, draggedEvent) => {
        // In month view, eventAllow uses midnight-based dates which causes
        // false rejections. Skip it here — validation is handled by _handleEventDrop.
        if (this.calendarViewSelected() === 'dayGridMonth') {
          return true;
        }

        if (draggedEvent?.start && this.isInPast(draggedEvent.start)) {
          return false;
        }

        if (this.isInPast(dropInfo.start)) {
          return false;
        }

        return true;
      },
      eventClassNames: (args) => {
        const { start } = args.event;

        if (start && this.isInPast(start)) {
          return ['event-past'];
        }

        return [];
      },
    })
  );

  protected readonly clientEventMap = new Map<
    ClientEventResponseModel['_id'],
    ClientEventResponseModel
  >();

  protected readonly calendarViewSelected = signal<CalendarViewType>(
    (localStorage.getItem('calendar-view-selected') as CalendarViewType) ??
      'dayGridMonth'
  );

  protected readonly calendarViewSelected$ = toObservable(this.calendarViewSelected);

  protected readonly calendarViewMap: Record<CalendarViewType, string> = {
    timeGridDay: 'Dag',
    timeGridWeek: 'Week',
    dayGridMonth: 'Maand',
    listWeek: 'Planning',
  };

  protected readonly now = startOfToday();

  private lastDateChangeSource: 'mat-calendar' | 'fullcalendar' | null = null;

  private isTodayClicked = false;

  private firstDatesSetEventTriggered = false;

  protected readonly currentCalendarDate = signal<Date>(startOfToday());

  private readonly _matCalendar = viewChild.required<MatCalendar<Date>>(MatCalendar);

  private readonly eventConflicts$ = this._clientId$.pipe(
    switchMap((clientId) => this._eventOperations.getConflicts(clientId))
  );

  protected readonly eventConflicts = this._eventOperations.eventConflicts;

  // Delegated to RangeClaimManagerService
  protected readonly clientEditors = this._rangeClaimManager.clientEditors;
  protected readonly renderedClientEditors =
    this._rangeClaimManager.renderedClientEditors;
  protected readonly currentUserId = this._rangeClaimManager.currentUserId;
  protected readonly currentClientEditor = this._rangeClaimManager.currentClientEditor;
  protected readonly blockedRanges = this._rangeClaimManager.blockedRanges;
  protected readonly hasBlockedRanges = this._rangeClaimManager.hasBlockedRanges;

  private readonly _wsConnection$ = this._wsService
    .connect<ClientEditorWebSocketPayload>('/event-stream', {
      requireAuth: true,
      topics: [`client/${this._clientId()}/editors`],
    })
    .pipe(takeUntilDestroyed(), shareReplay(1));

  private readonly _wsConnectionState$ = this._wsConnection$.pipe(
    switchMap((conn) => conn.connectionState$),
    filter((state) => state === ConnectionState.AUTHENTICATED),
    takeUntilDestroyed()
  );

  private readonly _wsEvents$ = this._wsConnection$.pipe(
    switchMap((conn) => conn.events$),
    takeUntilDestroyed()
  );

  private readonly _revoke$ = new Subject<void>();

  private _activeEventDialogRef: MatDialogRef<EventDialogComponent> | null = null;

  constructor() {
    this._revoke$
      .pipe(
        withLatestFrom(this._clientId$),
        switchMap(([, clientId]) =>
          this.isReadOnly ? EMPTY : this._clientEditorService.revoke(clientId)
        )
      )
      .subscribe();

    // Wait for WebSocket and calendarApi to be ready on first emission.
    combineLatest([
      this._calendarManager.calendarApi$.pipe(first()),
      this._wsConnectionState$.pipe(first()),
    ])
      .pipe(
        switchMap(([calendarApi]) =>
          forkJoin([
            this._fetchCalendarEvents(calendarApi),
            this.eventConflicts$,
            this._clientId$.pipe(
              switchMap((clientId) => this._clientEditorService.getEditors(clientId)),
              tap((result) => this.clientEditors.set(result))
            ),
            this.isReadOnly
              ? of(null)
              : this.claimRange({
                  start: calendarApi.view.activeStart,
                  end: calendarApi.view.activeEnd,
                }),
          ])
        )
      )
      .subscribe();

    this._calendarManager.calendarApi$
      .pipe(
        skip(1),
        switchMap((calendarApi) =>
          forkJoin([
            this._fetchCalendarEvents(calendarApi),
            this.eventConflicts$,
            this.isReadOnly
              ? of(null)
              : this.claimRange({
                  start: calendarApi.view.activeStart,
                  end: calendarApi.view.activeEnd,
                }),
          ])
        ),
        takeUntilDestroyed()
      )
      .subscribe();

    this._wsEvents$.pipe(takeUntil(this._revoke$)).subscribe((event) => {
      this._rangeClaimManager.setEditors(event.payload.editors);
    });

    this._wsEvents$
      .pipe(takeUntil(this._revoke$), debounceTime(500), pairwise())
      .subscribe({
        next: ([prev, curr]) => {
          const prevEditors = prev.payload.editors;
          const currEditors = curr.payload.editors;
          const currentUserId = this.currentUserId();

          // Check if auto-refresh should be triggered
          if (
            this._rangeClaimManager.shouldTriggerAutoRefresh(
              prevEditors,
              currEditors,
              currentUserId!
            )
          ) {
            this.refreshClaimInBackground();
          }
        },
      });

    this.calendarViewSelected$
      .pipe(
        pairwise(),
        tap(([previous, current]) => {
          this._calendarManager.changeView(current);

          localStorage.setItem('calendar-view-selected', current);

          const views: CalendarViewType[] = ['dayGridMonth', 'listWeek'];

          if (views.includes(previous) && !views.includes(current)) {
            this._calendarManager.scrollToCurrentTime();
          }
        })
      )
      .subscribe();

    const calendarViewSelected = localStorage.getItem('calendar-view-selected');

    if (calendarViewSelected) {
      this.calendarOptions.update((currentState) => ({
        ...currentState,
        initialView: calendarViewSelected,
      }));
    }

    this.startIdleDetection();

    interval(SchedulerComponent.ACTIVITY_UPDATE_INTERVAL_MS)
      .pipe(
        takeUntil(this._revoke$),
        takeUntilDestroyed(this._destroyRef),
        withLatestFrom(toObservable(this._idleDetectionService.status)),
        filter(([, status]) => status === 'active'),
        tap(() => this._sendActivityUpdate())
      )
      .subscribe();

    effect(() => {
      const blockedRanges = this.blockedRanges();
      this._calendarManager.renderBlockedRanges(blockedRanges);
    });
  }

  ngOnInit() {
    this._matCalendar()
      .selectedChange.pipe(filter((selectedDate) => selectedDate != null))
      .subscribe((selectedDate) => {
        this.handleDateSelectedChange(selectedDate);
      });
  }

  ngAfterViewInit() {
    const calendarApi = this._calendar().getApi();

    if (calendarApi) {
      this._calendarManager.setCalendarApi(calendarApi, false);
      this._calendarManager.updateSize();
      this._calendarManager.scrollToCurrentTime();
      this._calendarManager.setupWheelEventHandling(calendarApi.el, () =>
        this.calendarViewSelected()
      );
    }
  }

  ngOnDestroy() {
    this._revoke$.next();
    this._revoke$.complete();
  }

  next() {
    this._calendarManager.navigateNext();
  }

  previous() {
    this._calendarManager.navigatePrevious();
  }

  today() {
    this.isTodayClicked = true;
    this._calendarManager.navigateToToday();
  }

  setCalendarView(view: CalendarViewType) {
    this.calendarViewSelected.set(view);
  }

  sortCalendarViewOptions(
    a: KeyValue<CalendarViewType, string>,
    b: KeyValue<CalendarViewType, string>
  ) {
    const order: Record<CalendarViewType, number> = {
      timeGridDay: 0,
      timeGridWeek: 1,
      dayGridMonth: 2,
      listWeek: 3,
    };

    return order[a.key] - order[b.key];
  }

  handleEventCreate() {
    const { start, end } = this._calculateDefaultEventRange(new Date());

    this._calendarManager.selectDateRange(start, end);
  }

  handleCopyDay(): void {
    const calendarApi = this._calendar().getApi();
    const defaultSourceDate = startOfDay(new Date());

    const dialogRef = this._matDialog.open(CopyEventsDialogComponent, {
      data: {
        mode: 'day',
        defaultSourceDate,
      } satisfies CopyEventsDialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is CopyEventsDialogResult => result != null),
        withLatestFrom(this._clientId$),
        switchMap(([{ source, target }, clientId]) =>
          this._eventOperations.copyDayEvents(clientId, source, target).pipe(
            tap(() => this._calendarManager.setCalendarApi(calendarApi)),
            tap(() => this.eventConflicts$.subscribe()),
            tap(({ copied }) =>
              this._dialog.alert({
                title: copied > 0 ? 'Gekopieerd' : 'Geen events',
                message:
                  copied > 0
                    ? `${copied} event${copied === 1 ? '' : 's'} gekopieerd`
                    : 'Er zijn geen niet-terugkerende events op de geselecteerde dag',
                confirmText: 'OK',
              }),
            ),
            catchError(() => EMPTY),
          ),
        ),
      )
      .subscribe();
  }

  handleCopyWeek(): void {
    const calendarApi = this._calendar().getApi();
    const defaultSourceDate = startOfISOWeek(new Date());

    const dialogRef = this._matDialog.open(CopyEventsDialogComponent, {
      data: {
        mode: 'week',
        defaultSourceDate,
      } satisfies CopyEventsDialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is CopyEventsDialogResult => result != null),
        withLatestFrom(this._clientId$),
        switchMap(([{ source, target }, clientId]) =>
          this._eventOperations
            .copyWeekEvents(clientId, source, target)
            .pipe(
              tap(() => this._calendarManager.setCalendarApi(calendarApi)),
              tap(() => this.eventConflicts$.subscribe()),
              tap(({ copied }) =>
                this._dialog.alert({
                  title: copied > 0 ? 'Gekopieerd' : 'Geen events',
                  message:
                    copied > 0
                      ? `${copied} event${copied === 1 ? '' : 's'} gekopieerd`
                      : 'Er zijn geen niet-terugkerende events in de geselecteerde week',
                  confirmText: 'OK',
                }),
              ),
              catchError(() => EMPTY),
            ),
        ),
      )
      .subscribe();
  }

  handleEventUpdate(eventId: string, payload: ClientEventUpdateModel) {
    return of(payload).pipe(
      withLatestFrom(this._clientId$),
      switchMap(([data, clientId]) =>
        this._eventOperations.updateEvent(clientId, eventId, data)
      )
    );
  }

  handleEventDelete(eventId: string) {
    return of(eventId).pipe(
      withLatestFrom(this._clientId$),
      switchMap(([eventId, clientId]) =>
        this._eventOperations.deleteEvent(clientId, eventId)
      ),
      tap(() => {
        this.clientEventMap.delete(eventId);
      })
    );
  }

  handleDateSelectedChange(event: Date) {
    this.lastDateChangeSource = 'mat-calendar';

    this._matCalendar().selected = event;

    this._calendarManager.navigateToDate(event);
  }

  gotoConflict(conflict: ClientEventConflictResponseModel) {
    const startDate = new Date(conflict.startDate);
    const duration = differenceInMilliseconds(startDate, startOfDay(startDate));

    this._calendarManager.navigateToDate(startDate);
    this._calendarManager.scrollToTime(duration);
  }

  isInActiveRange(event: EventImpl, calendarApi: CalendarApi) {
    const { activeStart, activeEnd } = calendarApi.view;
    const { start, end } = event;

    if (!start || !end) {
      return false;
    }

    return start < activeEnd && end > activeStart;
  }

  doesIntervalOverlap(interval: { start: Date; end: Date }) {
    return this._eventOperations.doesIntervalOverlap(interval);
  }

  isInPast(start: Date) {
    return this._eventOperations.isInPast(start);
  }

  getIntervalOverlap(
    intervalLeft: { start: Date; end: Date },
    intervalRight: { start: Date; end: Date }
  ): { start: Date; end: Date } | null {
    return this._eventOperations.getIntervalOverlap(intervalLeft, intervalRight);
  }

  isDateInBlockedRange(date: Date): boolean {
    return this._rangeClaimManager.isDateInBlockedRange(date);
  }

  isIntervalInBlockedRange(start: Date, end: Date): boolean {
    return this._rangeClaimManager.isIntervalInBlockedRange(start, end);
  }

  claimRange(range: { start: Date; end: Date }) {
    return this._clientId$.pipe(
      switchMap((clientId) => this._rangeClaimManager.claimRange(clientId, range))
    );
  }

  refreshClaimInBackground() {
    if (this.isReadOnly) return;

    const clientId = this._clientId();
    if (!clientId) return;

    const range = this._calendarManager.getActiveRange();
    this._rangeClaimManager.refreshClaimInBackground(clientId, range).subscribe();
  }

  startIdleDetection() {
    this._idleDetectionService.configure({
      idleTime: 60,
      timeoutTime: 120,
    });

    this._idleDetectionService.start();
    this._idleDetectionService.registerCleanup(this._destroyRef);

    effect(() => {
      if (this._idleDetectionService.isTimedOut()) {
        this._idleDetectionService.stop();

        this._revoke$.next();
        this._revoke$.complete();

        if (this._activeEventDialogRef) {
          this._activeEventDialogRef.close();
          this._activeEventDialogRef = null;
        }

        this._dialog
          .confirm({
            title: 'Sessie verlopen',
            message: 'Uw sessie is verlopen wegens inactiviteit',
            confirmText: 'Refresh',
            cancelText: 'Ga terug',
          })
          .afterClosed()
          .subscribe({
            next: (confirmed) => {
              if (confirmed) {
                window.location.reload();

                return;
              }

              this._router.navigate(['clients', this._clientId()]);
            },
          });

        return;
      }
    });
  }

  private _sendActivityUpdate() {
    if (this.isReadOnly) return;

    const clientId = this._clientId();

    if (!clientId) return;

    this._clientEditorService
      .updateActivity(clientId)
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private _fetchCalendarEvents(calendarApi: CalendarApi) {
    const { view } = calendarApi;
    const { activeStart, activeEnd } = view;

    return this._clientId$.pipe(
      switchMap((clientId) =>
        this._eventOperations
          .fetchEventsByRange(clientId, {
            start: activeStart,
            end: activeEnd,
          })
          .pipe(
            tap({
              error: () =>
                this._dialog.alert({
                  title: `Foutmelding`,
                  message:
                    `Er is iets misgegaan bij het ophalen van de ingeplande interacties. ` +
                    `Probeer het later nog eens`,
                  confirmText: `OK`,
                }),
            }),
            takeUntilDestroyed(this._destroyRef),
            triggerIfSlow(() =>
              this._globalLoaders.setActive(
                SchedulerComponent.LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            ),
            catchError(() => of<ClientEventResponseModel[]>([])),
            finalize(() =>
              this._globalLoaders.setInactive(
                SchedulerComponent.LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            )
          )
      ),
      tap((response) => {
        this.clientEventMap.clear();
        response.forEach((value) => this.clientEventMap.set(value._id, value));
      }),
      tap(() => {
        // Use batch removal for better performance
        this._calendarManager.removeAllEventSources();
      }),
      map((response) => this._calendarManager.renderEvents(response)),
      tap((events) => {
        // Wrap in requestAnimationFrame to prevent blocking the main thread
        requestAnimationFrame(() => {
          this._calendarManager.updateEventSource(events);
        });
      })
    );
  }

  private _handleEventDidMount(args: EventMountArg): void {
    const { el, event, view } = args;

    // In list view, the full title is already visible — no tooltip needed.
    if (view.type === 'listWeek') {
      return;
    }

    const title = event.title;

    if (!title) {
      return;
    }

    // Use native title attribute for tooltips to avoid overlay elements
    // that intercept pointer events and break drag-and-drop in month view.
    el.title = title;

    // Disable drag and resize on past events.
    // Previously this was handled by `pointer-events: none` in CSS, but
    // that also blocked tooltips. Now we disable interactivity per-event
    // so the tooltip still works.
    if (event.start && this.isInPast(event.start)) {
      event.setProp('startEditable', false);
      event.setProp('durationEditable', false);
    }
  }

  private _handleEventClick(args: EventClickArg) {
    const { event, view } = args;
    const { id, start, end } = event;
    const { calendar: calendarApi } = view;

    const clientId = this._clientId();

    if (!clientId) {
      console.warn('There is not client id');

      return;
    }

    if (!start || !end) {
      return;
    }

    const isReadOnly = this.isReadOnly;

    if (!isReadOnly) {
      if (this.isInPast(start)) {
        event.setProp('classNames', [...event.classNames, 'event-past']);

        this._dialog.alert({
          title: 'Niet bewerkbaar',
          message: 'Dit event kan niet worden bewerkt omdat het in het verleden ligt',
          confirmText: 'OK',
        });

        return;
      }

      if (this.isIntervalInBlockedRange(start, end)) {
        const blockedBy = this.blockedRanges().find((range) =>
          areIntervalsOverlapping(
            { start, end },
            { start: range.start, end: range.end },
            { inclusive: true }
          )
        );

        this._dialog.alert({
          title: 'Niet bewerkbaar',
          message: blockedBy
            ? `Dit tijdvak is momenteel in gebruik door ${blockedBy.editorName}`
            : 'Dit tijdvak is momenteel in gebruik door een andere gebruiker',
          confirmText: 'OK',
        });

        return;
      }
    }

    const clientEvent = this.clientEventMap.get(id);

    if (!clientEvent) {
      console.warn(`Event with id ${id} not found in event map.`);

      return;
    }

    let recurrence = null;

    if (clientEvent.recurrence) {
      recurrence = {
        ...clientEvent.recurrence,
        interval: clientEvent.recurrence.interval ?? 1,
        bysetpos: clientEvent.recurrence.bysetpos ?? null,
        bymonthday: clientEvent.recurrence.bymonthday ?? null,
        until: subDays(clientEvent.recurrence.until, 1),
      };
    }

    const dialogMode = isReadOnly ? 'view' : 'edit';

    const dialogRef = this._eventDialog.open(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          map(
            (result): ClientEventUpdateModel => ({
              changes: {
                startDate: result.startDate,
                endDate: result.endDate,
                recurrence: result.recurrence,
              },
              delete: result.delete ?? false,
              occurrenceDate: event.start!,
            })
          ),
          switchMap((data) => {
            if (
              !data.delete &&
              this.isIntervalInBlockedRange(
                data.changes.startDate,
                data.changes.endDate
              )
            ) {
              dialogRef.close();

              this._dialog.alert({
                title: 'Niet bewerkbaar',
                message:
                  'Het is niet mogelijk om een dit event in een tijdvak te plaatsen dat al in gebruik is door een andere gebruiker',
                confirmText: 'OK',
              });

              return EMPTY;
            }

            if (data.delete && !clientEvent.recurrence) {
              return this.handleEventDelete(id);
            }

            if (clientEvent.recurrence) {
              return this._promptRecurrenceAction((action, data) => {
                if (action == 'multiple' && data.delete) {
                  // Only override until for deletions — the backend uses
                  // this to truncate the series at the occurrence.
                  // For edits the user's chosen end-date flows through
                  // and the backend uses occurrenceDate for truncation.
                  data.changes.recurrence = {
                    ...data.changes.recurrence!,
                    until: startOfDay(event.start!),
                  };
                }

                return this.handleEventUpdate(id, {
                  ...data,
                  action,
                });
              }, data);
            }

            return this.handleEventUpdate(id, data);
          }),
          tap(() => this._calendarManager.setCalendarApi(calendarApi))
        ),
      {
        start,
        end,
        clientId,
        recurrence: recurrence,
        interaction: clientEvent.interaction,
      },
      dialogMode
    );

    this._activeEventDialogRef = dialogRef;

    dialogRef.afterClosed().subscribe(() => {
      this._activeEventDialogRef = null;
    });
  }

  private _handleDateSelect(args: DateSelectArg) {
    const { view, start, end } = args;
    const { calendar: calendarApi } = view;
    const clientId = this._clientId();

    if (!clientId) {
      console.warn('There is not client id');

      return;
    }

    if (!this._canCreate) {
      this._calendarManager.unselect();

      return;
    }

    if (this.isIntervalInBlockedRange(start, end)) {
      this._dialog.alert({
        title: 'Niet bewerkbaar',
        message: 'Dit tijdvak is momenteel in gebruik door een andere gebruiker',
        confirmText: 'OK',
      });
      this._calendarManager.unselect();

      return;
    }

    const data = {
      start,
      end,
      clientId,
    };

    if (view.type === 'dayGridMonth') {
      const { start: newStart, end: newEnd } = this._calculateDefaultEventRange(start);

      data.start = newStart;
      data.end = newEnd;
    }

    const dialogRef = this._eventDialog.open(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          switchMap((result) => {
            if (this.isIntervalInBlockedRange(result.startDate, result.endDate)) {
              dialogRef.close();

              this._dialog.alert({
                title: 'Niet bewerkbaar',
                message:
                  'Het is niet mogelijk om een event in een tijdvak te plaatsen dat al in gebruik is door een andere gebruiker',
                confirmText: 'OK',
              });

              return EMPTY;
            }

            // Media events use a dedicated endpoint
            if (result.eventType !== 'interaction') {
              return this._clientId$.pipe(
                switchMap((cId) =>
                  this._eventOperations.createMediaEvent(cId, {
                    mediaId: result.mediaId,
                    mediaType: result.eventType === 'media-image' ? 'image' : 'audio',
                    startDate: result.startDate,
                    endDate: result.endDate,
                    recurrence: result.recurrence,
                  })
                ),
                tap(() => this._calendarManager.setCalendarApi(calendarApi))
              );
            }

            const isTemplate = result.interaction.clientId === null;

            const create$ = isTemplate
              ? this._clientId$.pipe(
                  switchMap((cId) =>
                    this._eventOperations.createEventFromTemplate(cId, {
                      templateId: result.interaction._id,
                      startDate: result.startDate,
                      endDate: result.endDate,
                      recurrence: result.recurrence,
                    })
                  )
                )
              : this._clientId$.pipe(
                  switchMap((cId) =>
                    this._eventOperations.createEvent(cId, {
                      startDate: result.startDate,
                      endDate: result.endDate,
                      recurrence: result.recurrence,
                      interactionId: result.interaction.correlationId,
                    })
                  )
                );

            return create$.pipe(
              tap(() => this._calendarManager.setCalendarApi(calendarApi))
            );
          })
        ),
      data
    );

    this._activeEventDialogRef = dialogRef;

    dialogRef.afterClosed().subscribe(() => {
      this._activeEventDialogRef = null;
      this._calendarManager.unselect();
    });
  }

  private _handleDateSelectAllow(args: DateSpanApi) {
    const { start, end } = args;

    if (this.calendarViewSelected() === 'dayGridMonth') {
      // In month view, start is midnight of the clicked day.
      // Only block if it's strictly before today (i.e. yesterday or earlier).
      if (start < startOfToday()) {
        return false;
      }
    } else if (this.isInPast(start)) {
      return false;
    }

    if (this.isIntervalInBlockedRange(start, end)) {
      return false;
    }

    if (['timeGridDay', 'timeGridWeek'].includes(this.calendarViewSelected())) {
      return !this.doesIntervalOverlap({ start, end });
    }

    return true;
  }

  private _handleDatesSet(args: DatesSetArg) {
    const { view } = args;
    const { calendar } = view;

    this.calendarTitle.set(view.title);

    const shouldSelectToday = this.isTodayClicked || !this.firstDatesSetEventTriggered;
    const selectedDate = shouldSelectToday ? this.now : view.currentStart;

    if (this.lastDateChangeSource === 'mat-calendar') {
      this.lastDateChangeSource = null;

      this._calendarManager.setCalendarApi(calendar);

      return;
    }

    this.isTodayClicked = false;
    this.firstDatesSetEventTriggered = true;
    this.lastDateChangeSource = 'fullcalendar';

    this._matCalendar().activeDate = selectedDate;
    this._matCalendar().selected = selectedDate;

    this._calendarManager.setCalendarApi(calendar);
  }

  private _handleEventResize(args: EventResizeDoneArg) {
    const { event, oldEvent, revert, view } = args;
    const { id, start, end } = event;
    const { calendar: calendarApi } = view;

    if (!start || !end) {
      return;
    }

    if (!this._canUpdate) {
      revert();

      return;
    }

    if (this.isInPast(start)) {
      revert();

      this._dialog.alert({
        title: 'Niet bewerkbaar',
        message: 'Dit event kan niet worden gewijzigd omdat het in het verleden ligt',
        confirmText: 'OK',
      });

      return;
    }

    if (this.isIntervalInBlockedRange(start, end)) {
      revert();
      this._dialog.alert({
        title: 'Niet bewerkbaar',
        message: 'Dit tijdvak is momenteel in gebruik door een andere gebruiker',
        confirmText: 'OK',
      });

      return;
    }

    const clientEvent = this.clientEventMap.get(id);

    if (!clientEvent) {
      return;
    }

    const nearest = this._calendarManager.roundToNearest5Minutes(
      secondsToMilliseconds(clientEvent.interaction.estimatedDuration)
    );

    const maxEnd = addMilliseconds(start, nearest);

    if (end < maxEnd) {
      revert();

      this._dialog.alert({
        title: 'Foutmelding',
        message: `Het is niet mogelijk om dit event kleiner te maken`,
        confirmText: 'OK',
      });

      return;
    }

    if (clientEvent.recurrence) {
      const eventRecurrence = clientEvent.recurrence;

      const dialogRef = this._showRecurrenceEditDialog((result) =>
        of(result).pipe(
          filter((result) => result !== undefined),
          map(
            (value): ClientEventUpdateModel => ({
              action: value as 'single' | 'multiple',
              occurrenceDate: oldEvent.start!,
              changes: {
                startDate: start,
                endDate: end,
                recurrence: {
                  ...eventRecurrence,
                  until: new Date(eventRecurrence.until),
                },
              },
            })
          ),
          switchMap((data) =>
            this.handleEventUpdate(id, data).pipe(
              tap(() => this._calendarManager.setCalendarApi(calendarApi)),
              catchError(() => {
                revert();

                return of(null);
              })
            )
          )
        )
      );

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (!confirmed) {
          revert();
        }
      });

      return;
    }

    this.handleEventUpdate(id, {
      changes: {
        startDate: start,
        endDate: end,
        recurrence: null,
      },
    })
      .pipe(
        tap(() => this._calendarManager.setCalendarApi(calendarApi)),
        catchError(() => {
          revert();

          return of(null);
        })
      )
      .subscribe();
  }

  private _handleEventDrop(args: EventDropArg) {
    const { event, oldEvent, revert, view } = args;
    const { id, start, end } = event;
    const { calendar: calendarApi } = view;

    if (!start || !end) {
      return;
    }

    if (!this._canUpdate) {
      revert();

      return;
    }

    const isPastDrop =
      view.type === 'dayGridMonth' ? start < startOfToday() : this.isInPast(start);

    if (isPastDrop) {
      revert();

      this._dialog.alert({
        title: 'Niet bewerkbaar',
        message: 'Dit event kan niet worden verplaatst naar het verleden',
        confirmText: 'OK',
      });

      return;
    }

    if (this.isIntervalInBlockedRange(start, end)) {
      revert();
      this._dialog.alert({
        title: 'Niet bewerkbaar',
        message: 'Dit tijdvak is momenteel in gebruik door een andere gebruiker',
        confirmText: 'OK',
      });

      return;
    }

    const clientEvent = this.clientEventMap.get(id);

    if (!clientEvent) {
      return;
    }

    if (clientEvent.recurrence) {
      const eventRecurrence = clientEvent.recurrence;

      const dialogRef = this._showRecurrenceEditDialog((result) =>
        of(result).pipe(
          filter((result) => result !== undefined),
          map(
            (value): ClientEventUpdateModel => ({
              action: value as 'single' | 'multiple',
              delete: false,
              changes: {
                startDate: start,
                endDate: end,
                recurrence: {
                  ...eventRecurrence,
                  until: new Date(eventRecurrence.until),
                },
              },
              occurrenceDate: oldEvent.start!,
            })
          ),
          switchMap((data) =>
            this.handleEventUpdate(id, data).pipe(
              tap(() => this._calendarManager.setCalendarApi(calendarApi)),
              catchError(() => {
                revert();

                return of(null);
              })
            )
          )
        )
      );

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (!confirmed) {
          revert();
        }
      });

      return;
    }

    this.handleEventUpdate(id, {
      changes: {
        startDate: start,
        endDate: end,
        recurrence: null,
      },
    })
      .pipe(
        tap(() => this._calendarManager.setCalendarApi(calendarApi)),
        catchError(() => {
          revert();

          return of(null);
        })
      )
      .subscribe();
  }

  private _calculateDefaultEventRange(
    date: Date,
    options: { intervalInMinutes?: number } = {}
  ): { start: Date; end: Date } {
    const { intervalInMinutes = 5 } = options;
    const elapsedTime = this._calendarManager.getTimeElapsedSinceToday();

    const farthest = farthestMinute(
      startOfDay(date).getTime() + elapsedTime,
      intervalInMinutes
    );

    const start = new Date(farthest);
    const end = addMinutes(start, intervalInMinutes);

    return { start, end };
  }

  private _showRecurrenceEditDialog(callback: ConfirmHandler<'single' | 'multiple'>) {
    return this._dialog.select({
      title: 'Terugkerend event bewerken',
      message: '',
      confirmText: 'OK',
      cancelText: 'Annuleren',
      options: [
        {
          label: 'Dit event',
          value: 'single',
          checked: true,
        },
        {
          label: 'Dit event en volgende events',
          value: 'multiple',
          checked: false,
        },
      ],
      onConfirm: callback,
    });
  }

  private _promptRecurrenceAction<T>(
    callback: (action: 'single' | 'multiple', data: T) => Observable<unknown>,
    data: T
  ) {
    return this._showRecurrenceEditDialog((action) =>
      of(action).pipe(
        filter((action) => action != null),
        switchMap((action) => callback(action, data))
      )
    ).afterClosed();
  }
}
